import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, CheckCircle, Loader2, AlertCircle, ArrowRight, Star, Shield, Headphones } from 'lucide-react';
import api from '../services/api';

const ReferralLanding = () => {
  const { referralCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [referralInfo, setReferralInfo] = useState(null);
  const [error, setError] = useState(null);

  // Single plan: 59€/year
  const plan = { name: '12 Mois', price: 59 };

  useEffect(() => {
    const loadReferralInfo = async () => {
      try {
        const response = await api.get(`/referral/${referralCode}/info`);
        setReferralInfo(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Code de parrainage invalide');
      } finally {
        setLoading(false);
      }
    };
    loadReferralInfo();
  }, [referralCode]);

  const handleChoosePlan = () => {
    // Redirect to registration with referral code
    navigate(`/inscription?ref=${referralCode}&plan=${plan.name}&price=${plan.price}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00FFFF]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0B] p-4">
        <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full text-center">
          <AlertCircle className="h-16 w-16 text-[#FF3B5C] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Lien invalide</h1>
          <p className="text-[#B8B8BA] mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            Retour à l&apos;accueil
          </button>
        </div>
      </div>
    );
  }

  const finalPrice = Math.max(0, plan.price - (referralInfo?.discount || 0));

  return (
    <div className="min-h-screen bg-[#0A0A0B] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-full px-4 py-2 mb-6">
            <Gift className="h-5 w-5 text-[#00FF88]" />
            <span className="text-[#00FF88] font-semibold">Offre de parrainage</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="text-[#00FF88]">{referralInfo?.referrer_name}</span> vous offre
          </h1>
          
          <div className="text-6xl md:text-7xl font-bold text-[#FF6B9D] mb-4">
            -{referralInfo?.discount}€
          </div>
          
          <p className="text-xl text-[#B8B8BA] max-w-2xl mx-auto">
            Profitez d&apos;une réduction exclusive sur votre abonnement IPTV4KFR grâce au parrainage de {referralInfo?.referrer_name} !
          </p>
        </motion.div>

        {/* Single Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <div className="relative bg-[#1A1A1D] rounded-lg metallic-border p-8 border-[#00FFFF] ring-2 ring-[#00FFFF]/20">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-[#00FFFF] text-black text-xs font-bold px-3 py-1 rounded-full">
                OFFRE UNIQUE
              </span>
            </div>
            
            <h3 className="text-2xl font-bold text-white text-center mb-6">Abonnement IPTV 12 Mois</h3>
            
            <div className="text-center mb-6">
              <div className="text-[#6B6B6E] line-through text-xl">{plan.price}€/an</div>
              <div className="text-5xl font-bold text-[#00FFFF] my-2">{finalPrice}€</div>
              <div className="text-[#00FF88] text-lg font-semibold">
                Vous économisez {referralInfo?.discount}€ !
              </div>
              <div className="text-[#B8B8BA] text-sm mt-2">Soit moins de {(finalPrice / 12).toFixed(2)}€/mois</div>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center space-x-3 text-[#B8B8BA]">
                <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0" />
                <span>+15 000 chaînes TV en direct</span>
              </li>
              <li className="flex items-center space-x-3 text-[#B8B8BA]">
                <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0" />
                <span>VOD illimitée (films et séries)</span>
              </li>
              <li className="flex items-center space-x-3 text-[#B8B8BA]">
                <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0" />
                <span>Qualité 4K HDR</span>
              </li>
              <li className="flex items-center space-x-3 text-[#B8B8BA]">
                <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0" />
                <span>Support technique 7j/7</span>
              </li>
              <li className="flex items-center space-x-3 text-[#B8B8BA]">
                <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0" />
                <span>Compatible tous appareils</span>
              </li>
            </ul>
            
            <button
              onClick={handleChoosePlan}
              className="w-full py-4 rounded-lg font-bold flex items-center justify-center space-x-2 transition-all bg-[#00FFFF] text-black hover:bg-[#00E5E5] text-lg"
            >
              <span>Profiter de l'offre</span>
              <ArrowRight className="h-5 w-5" />
            </button>
            
            <p className="text-[#6B6B6E] text-xs text-center mt-4">
              * Cette réduction est valable uniquement sur votre premier abonnement
            </p>
          </div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border text-center">
            <Star className="h-10 w-10 text-[#FFB800] mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">Qualité Premium</h3>
            <p className="text-[#B8B8BA] text-sm">Streaming en 4K HDR sans buffering</p>
          </div>
          
          <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border text-center">
            <Shield className="h-10 w-10 text-[#00FF88] mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">100% Sécurisé</h3>
            <p className="text-[#B8B8BA] text-sm">Paiement sécurisé et données protégées</p>
          </div>
          
          <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border text-center">
            <Headphones className="h-10 w-10 text-[#00FFFF] mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">Support 24/7</h3>
            <p className="text-[#B8B8BA] text-sm">Assistance technique disponible</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReferralLanding;
